import React, { useState, useEffect } from 'react';
import { Menu, X, Phone, ShoppingCart } from 'lucide-react';

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth' });
    setIsMenuOpen(false);
  };

  return (
    <>
      <header 
        dir="rtl" 
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-white/95 backdrop-blur-md shadow-sm py-1' : 'bg-transparent py-2'
        }`}
      >
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-14 md:h-16">
            {/* Logo */}
            <div className="flex items-center">
              <img
                src="https://i.ibb.co/n85QCGDP/totalelogo.png"
                alt="أمان توتال"
                className="h-10 w-auto md:h-14"
              />
            </div>
            
            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center">
              <div className="flex items-center space-x-reverse space-x-8">
                {[
                  { label: 'اطلب الآن', id: 'order-form' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`font-medium text-sm transition-colors hover:text-red-500 ${
                      isScrolled ? 'text-gray-700' : 'text-gray-800'
                    }`}
                  >
                    {item.label}
                  </button>
                ))}
              </div>
            </nav>
            
            {/* Desktop Contact & CTA */}
            <div className="hidden lg:flex items-center space-x-reverse space-x-3">
              <div className="flex items-center space-x-reverse space-x-2 text-gray-700">
                <Phone className="w-4 h-4" />
                <span dir="ltr" className="text-sm font-medium">+216 53 231 142</span>
              </div>
              <button
                onClick={() => scrollToSection('order-form')}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-full text-sm font-semibold transition-all duration-300 shadow-sm flex items-center space-x-reverse space-x-1"
              >
                <ShoppingCart className="w-3.5 h-3.5" />
                <span>اطلب الآن</span>
              </button>
            </div>
            
            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="md:hidden p-1.5 rounded-lg transition-colors text-gray-700 hover:bg-gray-100"
              aria-label="تبديل القائمة"
            >
              {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu - RTL Slide-in Panel */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/30 backdrop-blur-sm"
            onClick={() => setIsMenuOpen(false)}
          ></div>
          
          {/* Menu Panel - Slides from left in RTL */}
          <div className="absolute top-0 left-0 h-full w-4/5 max-w-xs bg-white shadow-xl transform transition-transform duration-300 ease-out">
            <div className="p-5">
              <div className="flex justify-between items-center mb-6">
                <img
                  src="https://i.ibb.co/n85QCGDP/totalelogo.png"
                  alt="أمان توتال"
                  className="h-10 w-auto"
                />
                <button
                  onClick={() => setIsMenuOpen(false)}
                  className="p-1.5 rounded-lg text-gray-700 hover:bg-gray-100"
                  aria-label="إغلاق القائمة"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <nav className="space-y-4">
                {[
                  { label: 'الرئيسية', id: 'hero' },
                  { label: 'المنتج', id: 'product-showcase' },
                  { label: 'كيفية العمل', id: 'how-it-works' },
                  { label: 'الأسعار', id: 'pricing' },
                  { label: 'اطلب الآن', id: 'order-form' }
                ].map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className="block w-full text-right py-3 px-4 text-gray-700 hover:bg-red-50 hover:text-red-500 rounded-lg font-medium transition-colors"
                  >
                    {item.label}
                  </button>
                ))}
              </nav>
              
              <div className="mt-8 pt-6 border-t border-gray-100">
                <div className="flex items-center justify-end space-x-reverse space-x-3 text-gray-700 mb-5">
                  <span dir="ltr" className="font-medium">+216 53 231 142</span>
                  <div className="bg-red-100 p-2 rounded-full">
                    <Phone className="w-4 h-4 text-red-500" />
                  </div>
                </div>
                <button
                  onClick={() => scrollToSection('order-form')}
                  className="w-full bg-red-500 hover:bg-red-600 text-white py-3 rounded-lg font-semibold flex items-center justify-center space-x-reverse space-x-2 transition-colors"
                >
                  <ShoppingCart className="w-4 h-4" />
                  <span>اطلب الآن</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;